import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Edit,
    Delete,
    Add,
    Cable,
    AutoAwesome,
} from '@mui/icons-material';
import { useTranslate, useNotify, useRefresh } from 'react-admin';
import { apiClient } from '../../utils/apiClient';
import { NumberField } from '../common/NumberField';
import { getSuggestedCurrent, formatCurrent } from '../../utils/powerCalculations';

interface Connector {
    id?: string;
    connectorId: number;
    connectorType: string;
    maxPower: number;
    maxCurrent?: number;
    status?: string;
    errorCode?: string;
    chargerTypeId?: string;
    chargerType?: {
        id: string;
        name: string;
        displayName: string;
        colorCode?: string;
        icon?: string;
    };
}

interface ConnectorsSectionProps {
    connectors: Connector[];
    onChange: (connectors: Connector[]) => void;
    chargerId?: string;
}

const CONNECTOR_TYPES = [
    { id: 'Type1', name: 'Type 1 (J1772)' },
    { id: 'Type2', name: 'Type 2' },
    { id: 'CCS1', name: 'CCS1 (Combo 1)' },
    { id: 'CCS2', name: 'CCS2 (Combo 2)' },
    { id: 'CHAdeMO', name: 'CHAdeMO' },
    { id: 'GBT', name: 'GB/T' },
];

export const ConnectorsSection: React.FC<ConnectorsSectionProps> = ({
    connectors = [],
    onChange,
    chargerId,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingConnector, setEditingConnector] = useState<Connector | null>(null);
    const [formData, setFormData] = useState<Partial<Connector>>({});
    const [saving, setSaving] = useState(false);
    const [chargerTypes, setChargerTypes] = useState<any[]>([]);

    const handleAdd = () => {
        const newConnectorId = Math.max(0, ...connectors.map(c => c.connectorId)) + 1;
        setFormData({
            connectorId: newConnectorId,
            connectorType: 'Type2',
            maxPower: 22,
            status: 'Unavailable',
        });
        setEditingConnector(null);
        setDialogOpen(true);
    };

    const handleEdit = (connector: Connector) => {
        // Ensure we capture the chargerTypeId correctly
        setFormData({
            ...connector,
            chargerTypeId: connector.chargerTypeId || connector.chargerType?.id
        });
        setEditingConnector(connector);
        setDialogOpen(true);
    };

    const handleDelete = async (connectorId: number) => {
        if (!window.confirm(`Delete connector #${connectorId}? This cannot be undone.`)) {
            return;
        }

        if (!chargerId) {
            // New charger (not yet saved) — just drop from local state
            onChange(connectors.filter(c => c.connectorId !== connectorId));
            notify('Connector removed', { type: 'info' });
            return;
        }

        try {
            await apiClient.delete(`/api/backend/connectors/${chargerId}/${connectorId}`);
            onChange(connectors.filter(c => c.connectorId !== connectorId));
            notify('Connector deleted successfully', { type: 'success' });
            refresh();
        } catch (error: any) {
            console.error('Error deleting connector:', error);
            const message = error?.body?.error || error?.message || 'Failed to delete connector';
            notify(message, { type: 'error' });
        }
    };

    const handleSave = async () => {
        if (!formData.connectorType || !formData.maxPower) {
            notify('Please fill in all required fields', { type: 'warning' });
            return;
        }

        if (!chargerId) {
            // If no chargerId yet (new charger), just update local state
            let updatedConnectors: Connector[];
            if (editingConnector) {
                updatedConnectors = connectors.map(c =>
                    c.connectorId === editingConnector.connectorId
                        ? { ...c, ...formData }
                        : c
                );
            } else {
                updatedConnectors = [...connectors, formData as Connector];
            }
            onChange(updatedConnectors);
            setDialogOpen(false);
            notify(editingConnector ? 'Connector updated' : 'Connector added', { type: 'success' });
            return;
        }

        // If we have a chargerId and editing an existing connector, call the API
        if (editingConnector) {
            setSaving(true);
            try {
                // Call the backend API to update the connector using apiClient
                const result = await apiClient.put<{ data: any }>(
                    `/api/backend/connectors/${chargerId}/${editingConnector.connectorId}`,
                    {
                        connectorType: formData.connectorType,
                        maxPower: formData.maxPower,
                        maxCurrent: formData.maxCurrent,
                        chargerTypeId: formData.chargerTypeId || null,
                    }
                );

                // Update local state with the response
                const updatedConnector = result.data;
                const updatedConnectors = connectors.map(c =>
                    c.connectorId === editingConnector.connectorId
                        ? {
                            ...c,
                            ...formData,
                            chargerType: updatedConnector?.charger_type,
                            chargerTypeId: updatedConnector?.charger_type_id,
                        }
                        : c
                );

                onChange(updatedConnectors);
                setDialogOpen(false);
                notify('Connector updated successfully', { type: 'success' });
                // Refresh the page to get updated data
                refresh();
            } catch (error) {
                console.error('Error updating connector:', error);
                notify('Failed to update connector', { type: 'error' });
            } finally {
                setSaving(false);
            }
        } else {
            // Adding a new connector to an existing charger — persist via POST
            setSaving(true);
            try {
                const result = await apiClient.post<{ data: any }>(
                    `/api/backend/connectors/${chargerId}`,
                    {
                        connectorId: formData.connectorId,
                        connectorType: formData.connectorType,
                        maxPower: formData.maxPower,
                        maxCurrent: formData.maxCurrent,
                        chargerTypeId: formData.chargerTypeId || null,
                        status: formData.status,
                    }
                );

                const created = result.data;
                const newConnector: Connector = {
                    ...(formData as Connector),
                    id: created?.id,
                    chargerType: created?.charger_type,
                    chargerTypeId: created?.charger_type_id,
                };

                onChange([...connectors, newConnector]);
                setDialogOpen(false);
                notify('Connector added successfully', { type: 'success' });
                refresh();
            } catch (error) {
                console.error('Error creating connector:', error);
                notify('Failed to add connector', { type: 'error' });
            } finally {
                setSaving(false);
            }
        }
    };

    const handleClose = () => {
        setDialogOpen(false);
        setFormData({});
        setEditingConnector(null);
    };

    // Fetch charger types when dialog opens
    React.useEffect(() => {
        if (dialogOpen) {
            apiClient.get<any>('/api/backend/charger-types').then(response => {
                const list = Array.isArray(response) ? response : response?.data;
                setChargerTypes(Array.isArray(list) ? list : []);
            }).catch(error => {
                console.error('Failed to load charger types:', error);
            });
        }
    }, [dialogOpen]);

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Cable sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.chargers.edit.connectors')}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleAdd}
                        sx={{
                            borderColor: '#00C853',
                            color: '#00C853',
                            '&:hover': {
                                borderColor: '#00A843',
                                backgroundColor: 'rgba(0, 200, 83, 0.08)',
                            },
                        }}
                    >
                        {translate('resources.chargers.edit.addConnector')}
                    </Button>
                </Box>

                {connectors.length === 0 ? (
                    <Alert severity="info">
                        {translate('resources.chargers.edit.noConnectors')}
                    </Alert>
                ) : (
                    isMobile ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {connectors.map((connector) => (
                                <Box
                                    key={connector.connectorId}
                                    sx={{
                                        p: 1.5,
                                        border: '1px solid #E0E0E0',
                                        borderRadius: 2,
                                        backgroundColor: '#FFFFFF',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', minWidth: 0, wordBreak: 'break-word' }}>
                                            #{connector.connectorId} · {connector.connectorType}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                            <IconButton size="small" onClick={() => handleEdit(connector)}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(connector.connectorId)}>
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('resources.chargers.fields.maxPower')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {connector.maxPower} kW
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('resources.chargers.fields.maxCurrent')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {connector.maxCurrent ? `${connector.maxCurrent} A` : '-'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                        {connector.chargerType ? (
                                            <Chip
                                                label={connector.chargerType.displayName}
                                                size="small"
                                                sx={{
                                                    backgroundColor: connector.chargerType.colorCode
                                                        ? `${connector.chargerType.colorCode}20`
                                                        : '#E0E0E0',
                                                    color: connector.chargerType.colorCode || '#000',
                                                    maxWidth: '100%',
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {translate('resources.chargers.fields.chargerType')}: -
                                            </Typography>
                                        )}

                                        <Chip
                                            label={connector.status || 'Unavailable'}
                                            size="small"
                                            color={connector.status === 'Available' ? 'success' : 'default'}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('resources.chargers.fields.connectorId')}</TableCell>
                                        <TableCell>{translate('resources.chargers.fields.connectorType')}</TableCell>
                                        <TableCell>{translate('resources.chargers.fields.maxPower')}</TableCell>
                                        <TableCell>{translate('resources.chargers.fields.maxCurrent')}</TableCell>
                                        <TableCell>{translate('resources.chargers.fields.chargerType')}</TableCell>
                                        <TableCell>{translate('resources.chargers.fields.status')}</TableCell>
                                        <TableCell align="right">{translate('common.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {connectors.map((connector) => (
                                        <TableRow key={connector.connectorId}>
                                            <TableCell>#{connector.connectorId}</TableCell>
                                            <TableCell>{connector.connectorType}</TableCell>
                                            <TableCell>{connector.maxPower} kW</TableCell>
                                            <TableCell>{connector.maxCurrent ? `${connector.maxCurrent} A` : '-'}</TableCell>
                                            <TableCell>
                                                {connector.chargerType ? (
                                                    <Chip
                                                        label={connector.chargerType.displayName}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: connector.chargerType.colorCode
                                                                ? `${connector.chargerType.colorCode}20`
                                                                : '#E0E0E0',
                                                            color: connector.chargerType.colorCode || '#000',
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={connector.status || 'Unavailable'}
                                                    size="small"
                                                    color={connector.status === 'Available' ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => handleEdit(connector)}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(connector.connectorId)}>
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                )}

                {/* Edit/Add Dialog */}
                <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {editingConnector
                            ? translate('resources.chargers.edit.editConnector')
                            : translate('resources.chargers.edit.addConnector')}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <NumberField
                                label={translate('resources.chargers.fields.connectorId')}
                                value={formData.connectorId}
                                onValueChange={(v) => setFormData({ ...formData, connectorId: v as any })}
                                integer
                                fullWidth
                                disabled={!!editingConnector}
                            />
                            <FormControl fullWidth>
                                <InputLabel>{translate('resources.chargers.fields.connectorType')}</InputLabel>
                                <Select
                                    value={formData.connectorType || ''}
                                    onChange={(e) => setFormData({ ...formData, connectorType: e.target.value })}
                                    label={translate('resources.chargers.fields.connectorType')}
                                >
                                    {CONNECTOR_TYPES.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <NumberField
                                label={translate('resources.chargers.fields.maxPower')}
                                value={formData.maxPower}
                                onValueChange={(v) => setFormData({ ...formData, maxPower: v as any })}
                                fullWidth
                                helperText="Maximum power in kW"
                            />
                            <Box sx={{ position: 'relative' }}>
                                <NumberField
                                    label={translate('resources.chargers.fields.maxCurrent')}
                                    value={formData.maxCurrent}
                                    onValueChange={(v) => setFormData({ ...formData, maxCurrent: v as any })}
                                    fullWidth
                                    helperText={(() => {
                                        const suggestedCurrent = formData.maxPower && formData.connectorType
                                            ? getSuggestedCurrent(formData.maxPower, formData.connectorType)
                                            : null;
                                        return suggestedCurrent
                                            ? translate('resources.chargers.edit.maxCurrentHelperTextWithSuggestion', {
                                                suggested: formatCurrent(suggestedCurrent)
                                            })
                                            : translate('resources.chargers.edit.maxCurrentHelperText');
                                    })()}
                                />
                                {formData.maxPower && formData.connectorType && (
                                    <Tooltip title={
                                        formData.maxCurrent && Math.abs(formData.maxCurrent - getSuggestedCurrent(formData.maxPower, formData.connectorType)!) < 1
                                            ? translate('resources.chargers.edit.autoCalculated')
                                            : translate('resources.chargers.edit.clickToAutoCalculate')
                                    }>
                                        <IconButton
                                            onClick={() => {
                                                const suggestedCurrent = getSuggestedCurrent(formData.maxPower!, formData.connectorType!);
                                                if (suggestedCurrent) {
                                                    setFormData({ ...formData, maxCurrent: suggestedCurrent });
                                                }
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                right: 8,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                marginTop: '-12px',
                                                padding: '4px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 200, 83, 0.08)',
                                                }
                                            }}
                                        >
                                            <AutoAwesome
                                                sx={{
                                                    color: formData.maxCurrent && Math.abs(formData.maxCurrent - getSuggestedCurrent(formData.maxPower!, formData.connectorType!)!) < 1
                                                        ? '#00C853'
                                                        : '#9E9E9E',
                                                    fontSize: 20,
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <FormControl fullWidth>
                                <InputLabel>{translate('resources.chargers.fields.chargerType')}</InputLabel>
                                <Select
                                    value={formData.chargerTypeId || ''}
                                    onChange={(e) => setFormData({ ...formData, chargerTypeId: e.target.value })}
                                    label={translate('resources.chargers.fields.chargerType')}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {chargerTypes.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.displayName || type.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={saving}>
                            {translate('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="primary"
                            disabled={saving || !formData.connectorType || !formData.maxPower}
                        >
                            {saving ? translate('common.saving') : translate('common.save')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};
